import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * ページ内のピル（ヘルプ・クレジット残高）の登録簿。
 *
 * App は「ページ右上にピルが無いページ」にだけ固定オーバーレイでピルを出す。
 * 以前はどのページが自前で持つかをパスの手書きリストで管理していたため、
 * 新しいページを足すたびに登録漏れ＝ピルの被りが再発していた。
 * 代わりに、ピルを描いている側が「自分は今、実際に表示されている」と申告する。
 *
 * 登録は id ベースの Set なので、React StrictMode の二重 effect（register →
 * cleanup → register）でもカウントが狂わない。
 */
interface HeaderActionsRegistry {
  register: (id: string) => void;
  unregister: (id: string) => void;
}

/** Provider の外では何もしない（部品単体のテストやショーケースで使えるように） */
const noopRegistry: HeaderActionsRegistry = {
  register: () => undefined,
  unregister: () => undefined,
};

// 登録用（値が変わらない）と読み取り用（boolean）を分け、
// 登録側が hasInline の変化で無駄に再描画されないようにする。
const HeaderActionsRegistryContext = createContext<HeaderActionsRegistry>(noopRegistry);
const HasInlineHeaderActionsContext = createContext(false);

export interface HeaderActionsProviderProps {
  children: React.ReactNode;
}

export const HeaderActionsProvider: React.FC<HeaderActionsProviderProps> = ({ children }) => {
  const idsRef = useRef<Set<string>>(new Set());
  const [hasInline, setHasInline] = useState(false);

  const register = useCallback((id: string) => {
    idsRef.current.add(id);
    setHasInline(idsRef.current.size > 0);
  }, []);

  const unregister = useCallback((id: string) => {
    idsRef.current.delete(id);
    setHasInline(idsRef.current.size > 0);
  }, []);

  const registry = useMemo<HeaderActionsRegistry>(
    () => ({ register, unregister }),
    [register, unregister]
  );

  return (
    <HeaderActionsRegistryContext.Provider value={registry}>
      <HasInlineHeaderActionsContext.Provider value={hasInline}>
        {children}
      </HasInlineHeaderActionsContext.Provider>
    </HeaderActionsRegistryContext.Provider>
  );
};

/** ページ内にピルが「実際に表示されている」間だけ true。固定オーバーレイの出し分けに使う。 */
export function useHasInlineHeaderActions(): boolean {
  return useContext(HasInlineHeaderActionsContext);
}

/**
 * インライン表示側が呼ぶ。visible が true の間だけ登録される。
 *
 * 固定オーバーレイのピルが 1 フレーム見えてから消える（ちらつく）のを避けるため、
 * 描画前に確定する useLayoutEffect で登録する。
 */
export function useRegisterInlineHeaderActions(visible: boolean): void {
  const registry = useContext(HeaderActionsRegistryContext);
  const id = useId();

  useLayoutEffect(() => {
    if (!visible) return undefined;
    registry.register(id);
    return () => registry.unregister(id);
  }, [visible, registry, id]);
}
